import React, { forwardRef, Fragment, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { Transitions } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Portal from '@polestar/component-warehouse-react/components/portal/portal';
import { AnimatePresence, m as motion, useMotionValue, useTransform, } from 'framer-motion';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import HiddenAccessibilityElement from '@polestar/component-warehouse-react/components/hidden-accessibility-element/HiddenAccessibilityElement';
import { useContentElement } from '@polestar/component-warehouse-react/contexts/contentElement';
import ResizeObserver from 'resize-observer-polyfill';
import { useScrollObserver } from '@polestar/component-warehouse-react/components/scroll-observer/ScrollObserver';
const ScrollProgress = forwardRef(({ detached = true, visible = true, progressLabel, progress, ...props }, ref) => {
    /**
     * These are the values needed when we use the progress of the page
     */
    const { scrollY, windowHeight, contentRef } = useContentElement();
    const contentHeight = useMotionValue(0);
    useEffect(() => {
        const handleUpdateHeight = () => {
            var _a;
            if (contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) {
                contentHeight.set((_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight);
            }
        };
        // Trigger on initialize
        handleUpdateHeight();
        if (contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) {
            const resizeObserver = new ResizeObserver(handleUpdateHeight);
            resizeObserver.observe(contentRef.current);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [contentHeight, contentRef]);
    const internalProgress = useTransform([scrollY, windowHeight, contentHeight], ([latestScrollY, latestWindowHeight, latestContentHeight]) => {
        return Math.min(1, Math.max(0, latestScrollY / (latestContentHeight - latestWindowHeight)));
    });
    /**
     * These are the values for when we are inside a ScrollObserver
     */
    const { showScrollProgressBar, addProgressListener, removeProgressListener } = useScrollObserver();
    const scrollObserverProgress = useMotionValue(typeof progress === 'number' ? progress : 0);
    useEffect(() => {
        // Skip when we get the value from outside
        if (progress) {
            return;
        }
        const listener = (progress) => {
            scrollObserverProgress.set(progress / 100);
        };
        addProgressListener(listener); //
        return () => {
            removeProgressListener(listener);
        };
    }, [addProgressListener, scrollObserverProgress, progress, removeProgressListener]);
    /**
     * When progress is set, just use that, instead of scrollobserver or page scroll
     */
    const finalProgress = (progress ||
        (showScrollProgressBar !== undefined ? scrollObserverProgress : null) ||
        internalProgress);
    const currentProgress = useTransform(finalProgress, [0, 1], [0, 100]);
    const handleRef = useRef(null);
    const hiddenRef = useRef(null);
    // Because we use a MotionValue (using state causes to much rerenders)
    // we need to update some of the aria values manually
    useEffect(() => {
        if (!progressLabel) {
            return;
        }
        const updateAria = (progress) => {
            if (handleRef.current) {
                handleRef.current.setAttribute('aria-valuenow', progress.toString());
                handleRef.current.setAttribute('aria-label', progressLabel(progress));
            }
            if (hiddenRef.current) {
                hiddenRef.current.textContent = progressLabel(progress);
            }
        };
        updateAria(currentProgress.get());
        return currentProgress.on('change', updateAria);
    }, [currentProgress, progressLabel]);
    const Wrapper = detached ? Portal : Fragment;
    return (React.createElement(Wrapper, null,
        React.createElement(AnimatePresence, null, visible && showScrollProgressBar !== false && (React.createElement(Container, { key: "scroll-progress", ...props, ref: ref, detached: detached, initial: {
                opacity: 0,
            }, animate: {
                opacity: 1,
            }, exit: {
                opacity: 0,
            }, transition: Transitions.default },
            React.createElement(ProgressHandleWrapper, null,
                React.createElement(ProgressHandle, { ref: handleRef, style: {
                        scaleX: finalProgress,
                    }, ...aria({
                        role: 'progressbar',
                        valuemin: 0,
                        valuemax: 100,
                    }) }),
                React.createElement(HiddenAccessibilityElement, { ref: hiddenRef, ...aria({ live: 'polite' }) })))))));
});
ScrollProgress.displayName = 'ScrollProgress';
export default ScrollProgress;
const Container = styled(motion.div)(({ detached }) => css(css `
      display: block;
      position: relative;
      width: 100%;
      z-index: 1;
    `, detached &&
    css `
        position: fixed;
        inset-block-start: 0;
        inset-inline-start: 0;
        inset-inline-end: 0;
      `));
Container.displayName = `Container`;
const progressHandleHeight = createCustomSpacing(4 / 16);
const ProgressHandle = styled(motion.div)(({ theme }) => css `
    display: block;
    width: 100%;
    height: ${progressHandleHeight};
    background-color: ${theme.scrollProgress.background};
    transform-origin: left;
  `);
const ProgressHandleWrapper = styled.div `
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  width: 100%;
  height: ${progressHandleHeight};
  z-index: 2;
  overflow: hidden;
`;
